/**
 * NavigationRegistry — zentrale typisierte Matrix für ALLE Immodossier-Routen.
 *
 * Jede im UI erreichbare Route-ID wird exakt einem main menu key und optional
 * einem submenu key zugeordnet. Kein includes, kein lokaler Click-State, kein
 * pending:true, keine duplizierte Matchlogik.
 *
 * Die Registry wird von __root.tsx (Sidebar-Active-State) und von Submenu-
 * Komponenten (PropertyEditSubmenu) als einzige Quelle für "welcher Tab ist
 * aktiv?" verwendet.
 */

import { AppRoute } from '@agentselly/immodossier-shared';

// ─── Main Menu Keys ──────────────────────────────────────────────────────────

export type MainMenuKey =
  | 'home'
  | 'dossier'
  | 'cockpit'
  | 'contact'
  | 'demo'
  | 'admin'
  | 'auth'
  | 'other';

// ─── Submenu Keys ────────────────────────────────────────────────────────────

export type SubmenuKey =
  // Dossier Submenu
  | 'dossier_home'
  | 'dossier_evaluation'
  | 'dossier_region'
  | 'dossier_market'
  // Cockpit Submenu
  | 'cockpit_home'
  | 'cockpit_activities'
  | 'cockpit_calendar'
  | 'cockpit_interest'
  | 'cockpit_offers'
  | 'cockpit_statistics'
  | 'cockpit_documents'
  // Property Edit Submenu
  | 'property_edit'
  | 'property_condition'
  | 'property_quality'
  // Admin Submenu
  | 'admin_home'
  | 'admin_evaluations'
  | 'admin_users'
  // Demo Submenu
  | 'demo_dossier'
  | 'demo_cockpit'
  // Contact
  | 'contact_main';

// ─── Registry Entry ──────────────────────────────────────────────────────────

export interface NavigationEntry {
  readonly routeId: AppRoute;
  readonly main: MainMenuKey;
  readonly sub?: SubmenuKey;
}

// ─── Registry ─────────────────────────────────────────────────────────────────

/**
 * Vollständige Matrix aller Immodossier-Routen mit main/submenu-Zuordnung.
 * Jede Route erscheint EXAKT EINMAL. Keine Duplikate, kein fuzzy-Match.
 */
export const NAVIGATION_REGISTRY: readonly NavigationEntry[] = [
  // ── Home ────────────────────────────────────────────────────────────────
  { routeId: AppRoute.ROOT, main: 'home' },
  { routeId: AppRoute.$LANG_$COMPANYID_INDEX, main: 'home' },
  { routeId: AppRoute.$LANG_$COMPANYID_$CONTACTID_INDEX, main: 'home' },

  // ── Dossier ──────────────────────────────────────────────────────────────
  {
    routeId: AppRoute.$LANG_$COMPANYID_$CONTACTID_DOSSIER_$DOSSIERID_HOME,
    main: 'dossier',
    sub: 'dossier_home',
  },
  {
    routeId: AppRoute.$LANG_$COMPANYID_$CONTACTID_DOSSIER_$DOSSIERID_EVALUATION,
    main: 'dossier',
    sub: 'dossier_evaluation',
  },
  {
    routeId: AppRoute.$LANG_$COMPANYID_$CONTACTID_DOSSIER_$DOSSIERID_REGION,
    main: 'dossier',
    sub: 'dossier_region',
  },
  {
    routeId: AppRoute.$LANG_$COMPANYID_$CONTACTID_DOSSIER_$DOSSIERID_MARKET,
    main: 'dossier',
    sub: 'dossier_market',
  },

  // ── Property Edit (Dossier-Submenu) ─────────────────────────────────────
  {
    routeId:
      AppRoute.$LANG_$COMPANYID_$CONTACTID_DOSSIER_$DOSSIERID_PROPERTY_EDIT,
    main: 'dossier',
    sub: 'property_edit',
  },
  {
    routeId:
      AppRoute.$LANG_$COMPANYID_$CONTACTID_DOSSIER_$DOSSIERID_PROPERTY_CONDITION_EDIT,
    main: 'dossier',
    sub: 'property_condition',
  },
  {
    routeId:
      AppRoute.$LANG_$COMPANYID_$CONTACTID_DOSSIER_$DOSSIERID_PROPERTY_QUALITIES_EDIT,
    main: 'dossier',
    sub: 'property_quality',
  },

  // ── Cockpit ──────────────────────────────────────────────────────────────
  {
    routeId: AppRoute.$LANG_$COMPANYID_$CONTACTID_COCKPIT_$PROPERTYID_HOME,
    main: 'cockpit',
    sub: 'cockpit_home',
  },
  {
    routeId:
      AppRoute.$LANG_$COMPANYID_$CONTACTID_COCKPIT_$PROPERTYID_ACTIVITIES,
    main: 'cockpit',
    sub: 'cockpit_activities',
  },
  {
    routeId: AppRoute.$LANG_$COMPANYID_$CONTACTID_COCKPIT_$PROPERTYID_CALENDAR,
    main: 'cockpit',
    sub: 'cockpit_calendar',
  },
  {
    routeId: AppRoute.$LANG_$COMPANYID_$CONTACTID_COCKPIT_$PROPERTYID_INTEREST,
    main: 'cockpit',
    sub: 'cockpit_interest',
  },
  {
    routeId: AppRoute.$LANG_$COMPANYID_$CONTACTID_COCKPIT_$PROPERTYID_OFFERS,
    main: 'cockpit',
    sub: 'cockpit_offers',
  },
  {
    routeId:
      AppRoute.$LANG_$COMPANYID_$CONTACTID_COCKPIT_$PROPERTYID_STATISTICS,
    main: 'cockpit',
    sub: 'cockpit_statistics',
  },
  {
    routeId: AppRoute.$LANG_$COMPANYID_$CONTACTID_COCKPIT_$PROPERTYID_DOCUMENTS,
    main: 'cockpit',
    sub: 'cockpit_documents',
  },

  // ── Contact ──────────────────────────────────────────────────────────────
  {
    routeId: AppRoute.$LANG_$COMPANYID_$CONTACTID_CONTACT,
    main: 'contact',
    sub: 'contact_main',
  },
  {
    routeId: AppRoute.$LANG_$COMPANYID_PUBLIC_CONTACT,
    main: 'contact',
    sub: 'contact_main',
  },

  // ── Demo ─────────────────────────────────────────────────────────────────
  {
    routeId: AppRoute.$LANG_$COMPANYID_PUBLIC_DEMO_INDEX,
    main: 'demo',
  },
  {
    routeId: AppRoute.$LANG_$COMPANYID_PUBLIC_DEMO_DOSSIER_HOME,
    main: 'demo',
    sub: 'demo_dossier',
  },
  {
    routeId: AppRoute.$LANG_$COMPANYID_PUBLIC_DEMO_DOSSIER_EVALUATION,
    main: 'demo',
    sub: 'demo_dossier',
  },
  {
    routeId: AppRoute.$LANG_$COMPANYID_PUBLIC_DEMO_DOSSIER_REGION,
    main: 'demo',
    sub: 'demo_dossier',
  },
  {
    routeId: AppRoute.$LANG_$COMPANYID_PUBLIC_DEMO_DOSSIER_MARKET,
    main: 'demo',
    sub: 'demo_dossier',
  },
  {
    routeId: AppRoute.$LANG_$COMPANYID_PUBLIC_DEMO_COCKPIT_HOME,
    main: 'demo',
    sub: 'demo_cockpit',
  },
  {
    routeId: AppRoute.$LANG_$COMPANYID_PUBLIC_DEMO_COCKPIT_ACTIVITIES,
    main: 'demo',
    sub: 'demo_cockpit',
  },
  {
    routeId: AppRoute.$LANG_$COMPANYID_PUBLIC_DEMO_COCKPIT_CALENDAR,
    main: 'demo',
    sub: 'demo_cockpit',
  },
  {
    routeId: AppRoute.$LANG_$COMPANYID_PUBLIC_DEMO_COCKPIT_DOCUMENTS,
    main: 'demo',
    sub: 'demo_cockpit',
  },
  {
    routeId: AppRoute.$LANG_$COMPANYID_PUBLIC_DEMO_COCKPIT_INTEREST,
    main: 'demo',
    sub: 'demo_cockpit',
  },
  {
    routeId: AppRoute.$LANG_$COMPANYID_PUBLIC_DEMO_COCKPIT_OFFERS,
    main: 'demo',
    sub: 'demo_cockpit',
  },
  {
    routeId: AppRoute.$LANG_$COMPANYID_PUBLIC_DEMO_COCKPIT_STATISTICS,
    main: 'demo',
    sub: 'demo_cockpit',
  },

  // ── Admin ────────────────────────────────────────────────────────────────
  {
    routeId: AppRoute.$LANG_$COMPANYID_ADMIN_HOME,
    main: 'admin',
    sub: 'admin_home',
  },
  {
    routeId: AppRoute.$LANG_$COMPANYID_ADMIN_EVALUATIONS,
    main: 'admin',
    sub: 'admin_evaluations',
  },
  {
    routeId: AppRoute.$LANG_$COMPANYID_ADMIN_USERS,
    main: 'admin',
    sub: 'admin_users',
  },

  // ── Auth / Other ─────────────────────────────────────────────────────────
  { routeId: AppRoute.AUTH_FAILURE, main: 'auth' },
  { routeId: AppRoute.AUTH_OAUTH, main: 'auth' },
  { routeId: AppRoute.AUTH_SUCCESS, main: 'auth' },
  { routeId: AppRoute.SSO_CALLBACK_$PROVIDER, main: 'auth' },
  { routeId: AppRoute.$LANG_$COMPANYID_PUBLIC_AUTH_LOGIN, main: 'auth' },
  { routeId: AppRoute.$LANG_$COMPANYID_PUBLIC_AUTH_REGISTER, main: 'auth' },
  { routeId: AppRoute.$LANG_$COMPANYID_PUBLIC_AUTH_WAITLIST, main: 'auth' },

  // ── Other (Profile, Preference Center, Partner, etc.) ────────────────────
  {
    routeId: AppRoute.$LANG_$COMPANYID_$CONTACTID_PROFILE,
    main: 'other',
  },
  {
    routeId: AppRoute.$LANG_$COMPANYID_$CONTACTID_PREFERENCE_CENTER,
    main: 'other',
  },
  {
    routeId: AppRoute.$LANG_$COMPANYID_PARTNER_$EXTERNALREFERENCE_RESULT,
    main: 'other',
  },
  {
    routeId: AppRoute.$LANG_$COMPANYID_PUBLIC_EVALUATION_RESULT_$SESSIONID,
    main: 'other',
  },
  {
    routeId:
      AppRoute.$LANG_$COMPANYID_PUBLIC_EXPOSE_$CONTACTID_$PROPERTYID_BOOK,
    main: 'other',
  },
  {
    routeId:
      AppRoute.$LANG_$COMPANYID_PUBLIC_EXPOSE_$CONTACTID_$PROPERTYID_VIEW,
    main: 'other',
  },
  {
    routeId: AppRoute.$LANG_$COMPANYID_PUBLIC_SALES_CALL_SESSION,
    main: 'other',
  },
  {
    routeId: AppRoute.$LANG_$COMPANYID_PUBLIC_VALIANT_$VALIANTDEVICEID,
    main: 'other',
  },
  {
    routeId: AppRoute.VALIANT,
    main: 'other',
  },
] as const;

// ─── Lookup Helpers ──────────────────────────────────────────────────────────

/**
 * Lookup-Tabelle: AppRoute → NavigationEntry (O(1)).
 */
const registryByRouteId = new Map<AppRoute, NavigationEntry>(
  NAVIGATION_REGISTRY.map((entry) => [
    entry.routeId,
    entry,
  ]),
);

/**
 * Gibt den NavigationEntry für eine gegebene Route-ID zurück.
 * Returns `undefined` für nicht-registrierte Routen.
 */
export function getNavigationEntry(
  routeId: AppRoute | string,
): NavigationEntry | undefined {
  return registryByRouteId.get(routeId as AppRoute);
}

/**
 * Gibt den MainMenuKey für eine Route-ID zurück.
 * Fallback: 'other' für unbekannte Routen.
 */
export function getMainMenuKey(routeId: AppRoute | string): MainMenuKey {
  return registryByRouteId.get(routeId as AppRoute)?.main ?? 'other';
}

/**
 * Gibt den SubmenuKey für eine Route-ID zurück.
 * Returns `undefined` wenn die Route kein Submenu hat.
 */
export function getSubmenuKey(
  routeId: AppRoute | string,
): SubmenuKey | undefined {
  return registryByRouteId.get(routeId as AppRoute)?.sub;
}

/**
 * Prüft, ob eine Route-ID ein Property-Edit-Tab ist.
 */
export function isPropertyEditRoute(routeId: AppRoute | string): boolean {
  const sub = getSubmenuKey(routeId);
  return (
    sub === 'property_edit' ||
    sub === 'property_condition' ||
    sub === 'property_quality'
  );
}

/**
 * Gibt den passenden Fallback-Hash für den Back-Button auf Property-Edit-Seiten.
 */
export function getPropertyEditFallbackHash(
  routeId: AppRoute | string,
): string {
  const sub = getSubmenuKey(routeId);
  if (sub === 'property_condition') return 'evaluationPropertyConditions';
  if (sub === 'property_quality') return 'evaluationPropertyQuality';
  return 'evaluationPropertyInformation';
}
